import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Armchair, ChevronDown, Loader2, Luggage, Sparkles, UtensilsCrossed } from "lucide-react";
import { fetchFlightAncillaries, saveFlightAncillaries, formatApiError, inr } from "@/lib/api";

/* Visual seat map for one segment. Seat states:
   booked = grey/disabled, selected = saffron, free = green tint, paid = white. */
function SeatMap({ segKey, map, selectedCode, onPick, paxKey }) {
    const { rows, seats } = map;
    const byPos = useMemo(() => {
        const m = {};
        for (const s of seats) m[`${s.row}-${s.col}`] = s;
        return m;
    }, [seats]);
    const maxCol = useMemo(() => Math.max(0, ...seats.map((s) => s.col)), [seats]);
    if (!seats.length) {
        return <p className="text-xs text-stone-400 italic">Seat map isn't available for this sector.</p>;
    }
    const gridRows = [];
    for (let r = 1; r <= rows; r++) {
        const cells = [];
        let prevCol = 0;
        for (let c = 1; c <= maxCol; c++) {
            if (prevCol && c - prevCol > 1) {
                cells.push(<div key={`aisle-${r}-${c}`} className="w-4 sm:w-5" />); // aisle gap
            }
            prevCol = c;
            const s = byPos[`${r}-${c}`];
            if (!s) { cells.push(<div key={`${r}-${c}`} className="w-8 sm:w-9" />); continue; }
            const selected = selectedCode === s.code;
            cells.push(
                <button key={s.code} type="button" disabled={s.booked}
                    onClick={() => onPick(segKey, s)}
                    data-testid={`seat-${paxKey}-${segKey}-${s.seat_no}`}
                    title={`${s.seat_no} · ${s.amount ? inr(s.amount) : "Free"}${s.legroom ? " · Extra legroom" : ""}${s.exit_row ? " · Exit row" : ""}`}
                    className={`w-8 sm:w-9 h-9 rounded-t-lg rounded-b-md text-[10px] font-black border transition-all flex flex-col items-center justify-center leading-none
                        ${s.booked ? "bg-stone-200 border-stone-300 text-stone-400 cursor-not-allowed"
                        : selected ? "bg-saffron border-saffron text-white scale-110 shadow-md"
                        : s.amount === 0 ? "bg-green-50 border-green-300 text-green-700 hover:border-saffron"
                        : "bg-white border-stone-300 text-navy hover:border-saffron hover:-translate-y-0.5"}`}>
                    <span>{s.seat_no}</span>
                    <span className={`text-[8px] font-bold ${selected ? "text-white/90" : s.booked ? "text-stone-300" : "text-stone-400"}`}>
                        {s.booked ? "✕" : s.amount ? `₹${s.amount}` : "FREE"}
                    </span>
                </button>
            );
        }
        gridRows.push(
            <div key={r} className="flex items-center gap-1">
                <span className="w-5 text-[9px] font-bold text-stone-300 text-right">{r}</span>
                <div className="flex gap-1">{cells}</div>
            </div>
        );
    }
    return (
        <div className="rounded-2xl border border-stone-200 bg-gradient-to-b from-stone-50 to-white p-3 overflow-x-auto" data-testid={`seatmap-${paxKey}-${segKey}`}>
            <div className="mx-auto w-fit">
                <div className="mx-auto mb-2 h-5 w-3/4 rounded-b-[100%] border-b-2 border-stone-300 text-center text-[9px] font-black uppercase tracking-widest text-stone-400">Front</div>
                <div className="space-y-1">{gridRows}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 justify-center text-[10px] font-bold text-stone-500">
                <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-white border border-stone-300 inline-block" /> Paid</span>
                <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-green-50 border border-green-300 inline-block" /> Free</span>
                <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-saffron inline-block" /> Your pick</span>
                <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-stone-200 inline-block" /> Taken</span>
            </div>
        </div>
    );
}

/* Meals / extra-baggage option list for one segment (radio behaviour per type). */
function OptionList({ icon: Icon, title, items, selectedCode, onPick, testPrefix }) {
    if (!items.length) return null;
    return (
        <div data-testid={testPrefix}>
            <p className="text-xs font-black uppercase tracking-wider text-stone-500 flex items-center gap-1.5 mb-2">
                <Icon className="h-3.5 w-3.5 text-saffron" /> {title}
            </p>
            <div className="flex flex-wrap gap-2">
                {items.map((m) => {
                    const on = selectedCode === m.code;
                    return (
                        <button key={m.code} type="button" onClick={() => onPick(m)}
                            data-testid={`${testPrefix}-${m.code}`}
                            className={`rounded-xl border px-3 py-2 text-left text-xs transition-all ${on
                                ? "border-saffron bg-saffron/10 shadow-sm"
                                : "border-stone-200 bg-white hover:border-saffron/60"}`}>
                            <span className={`block font-bold ${on ? "text-saffron" : "text-navy"}`}>{m.desc || m.code}</span>
                            <span className="block text-[10px] font-semibold text-stone-400 mt-0.5">{m.amount ? inr(m.amount) : "Free"}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function FlightExtrasPanel({ reference, onSaved }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [paxIdx, setPaxIdx] = useState(0);
    const [selections, setSelections] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchFlightAncillaries(reference)
            .then((d) => {
                if (!d.available) { setError(d.reason); return; }
                setData(d);
                setSelections(d.selections || []);
            })
            .catch(() => setError("Extras couldn't be loaded — you can still pay without them"));
    }, [reference]);

    const extrasTotal = useMemo(() => selections.reduce((s, x) => s + x.amount, 0), [selections]);

    const pick = (segKey, legIdx, type, code, label, amount) => {
        setSelections((cur) => {
            const without = cur.filter((s) => !(s.leg_index === legIdx && s.segment_key === segKey
                && s.passenger_index === paxIdx && s.type === type));
            const wasSelected = cur.some((s) => s.leg_index === legIdx && s.segment_key === segKey
                && s.passenger_index === paxIdx && s.type === type && s.code === code);
            if (wasSelected) return without; // tap again to remove
            return [...without, { leg_index: legIdx, segment_key: segKey, passenger_index: paxIdx, type, code, label, amount }];
        });
    };

    const selFor = (legIdx, segKey, type) =>
        selections.find((s) => s.leg_index === legIdx && s.segment_key === segKey
            && s.passenger_index === paxIdx && s.type === type);

    // seats taken by OTHER passengers in this session (prevent double-pick)
    const seatTakenByOther = (legIdx, segKey, code) =>
        selections.some((s) => s.leg_index === legIdx && s.segment_key === segKey
            && s.type === "seat" && s.code === code && s.passenger_index !== paxIdx);

    const save = async () => {
        setSaving(true);
        try {
            const r = await saveFlightAncillaries(reference, selections);
            onSaved?.(r.booking);
            toast.success(extrasTotal ? `Extras added — new total updated` : "Extras cleared");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    if (error) return null; // extras simply don't appear — booking flow untouched
    if (!data) return (
        <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-center" data-testid="extras-loading">
            <Loader2 className="h-4 w-4 animate-spin text-saffron inline-block" />
            <span className="ml-2 text-xs text-stone-400">Checking seat & meal options…</span>
        </div>
    );

    const hasAnything = data.legs.some((l) => l.segments.some((s) => s.meals.length || s.baggage.length))
        || Object.keys(data.seat_maps || {}).length > 0;
    if (!hasAnything) return null;

    return (
        <div className="mt-4 rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid="extras-panel">
            <button type="button" onClick={() => setOpen(!open)} data-testid="extras-toggle"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-cream/60 transition-colors">
                <span className="flex items-center gap-2 font-bold text-navy text-sm">
                    <Sparkles className="h-4 w-4 text-saffron" />
                    Add seats, meals & extra baggage
                    <span className="text-[10px] font-black uppercase tracking-wider text-green-600 bg-green-50 rounded-full px-2 py-0.5">Optional</span>
                </span>
                <span className="flex items-center gap-2 text-xs font-bold text-stone-500">
                    {extrasTotal > 0 && <span className="text-saffron" data-testid="extras-total">+{inr(extrasTotal)}</span>}
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>

            {open && (
                <div className="border-t border-stone-100 p-4 space-y-5">
                    {/* passenger chips */}
                    {data.passengers.length > 1 && (
                        <div className="flex flex-wrap gap-2" data-testid="extras-pax-tabs">
                            {data.passengers.map((p) => (
                                <button key={p.index} type="button" onClick={() => setPaxIdx(p.index)}
                                    data-testid={`extras-pax-${p.index}`}
                                    className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${paxIdx === p.index
                                        ? "bg-navy text-white" : "bg-cream text-stone-500 hover:bg-stone-200"}`}>
                                    {p.name || `${p.type} ${p.index + 1}`}
                                </button>
                            ))}
                        </div>
                    )}

                    {data.legs.map((leg) => (
                        <div key={leg.leg_index} className="space-y-4">
                            {data.legs.length > 1 && (
                                <p className="text-xs font-black uppercase tracking-wider text-navy">
                                    {leg.route} · {leg.date}
                                </p>
                            )}
                            {leg.segments.map((seg) => {
                                const segMap = (data.seat_maps?.[String(leg.leg_index)] || {})[seg.key];
                                return (
                                    <div key={seg.key} className="rounded-xl bg-cream/40 border border-stone-100 p-3 space-y-4">
                                        <p className="text-[11px] font-bold text-stone-500">
                                            {seg.from} → {seg.to} · {seg.flight_no}
                                        </p>
                                        {segMap && (
                                            <div>
                                                <p className="text-xs font-black uppercase tracking-wider text-stone-500 flex items-center gap-1.5 mb-2">
                                                    <Armchair className="h-3.5 w-3.5 text-saffron" /> Pick a seat
                                                    {selFor(leg.leg_index, seg.key, "seat") &&
                                                        <span className="text-saffron normal-case">— {selFor(leg.leg_index, seg.key, "seat").label}</span>}
                                                </p>
                                                <SeatMap segKey={seg.key} map={segMap} paxKey={paxIdx}
                                                    selectedCode={selFor(leg.leg_index, seg.key, "seat")?.code}
                                                    onPick={(sk, s) => {
                                                        if (seatTakenByOther(leg.leg_index, sk, s.code)) {
                                                            toast.error("That seat is picked for another traveller");
                                                            return;
                                                        }
                                                        pick(sk, leg.leg_index, "seat", s.code, `Seat ${s.seat_no}`, s.amount);
                                                    }} />
                                            </div>
                                        )}
                                        <OptionList icon={UtensilsCrossed} title="Meals" items={seg.meals}
                                            testPrefix={`meal-${paxIdx}-${seg.key}`}
                                            selectedCode={selFor(leg.leg_index, seg.key, "meal")?.code}
                                            onPick={(m) => pick(seg.key, leg.leg_index, "meal", m.code, m.desc, m.amount)} />
                                        <OptionList icon={Luggage} title="Extra baggage" items={seg.baggage.filter((b) => b.amount > 0)}
                                            testPrefix={`bag-${paxIdx}-${seg.key}`}
                                            selectedCode={selFor(leg.leg_index, seg.key, "baggage")?.code}
                                            onPick={(b) => pick(seg.key, leg.leg_index, "baggage", b.code, b.desc, b.amount)} />
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    <div className="flex items-center justify-between gap-3 rounded-xl bg-cream border border-stone-200 px-4 py-3">
                        <div className="text-xs text-stone-500">
                            <span className="font-bold text-navy">Extras total: </span>
                            <span className="font-display font-black text-saffron text-sm" data-testid="extras-panel-total">{inr(extrasTotal)}</span>
                            <span className="block text-[10px] mt-0.5">Non-refundable once booked · seat/meal availability is airline-controlled</span>
                        </div>
                        <button type="button" onClick={save} disabled={saving} data-testid="extras-save-btn"
                            className="rounded-full bg-navy text-white font-bold px-5 py-2.5 text-sm hover:bg-saffron transition-colors disabled:opacity-50 flex items-center gap-2">
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Update total
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
